import { useGetSystemConditionAndRiskById } from "@features/ax/assetRegistry/facilities/hooks";
import { InfoTooltip } from "@features/ax/assetRegistry/facilities/shared/InfoToolTip";
import {
  useAssessCalculation,
  usegetConditionHistory,
} from "@features/ax/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface ConditionsRisksTabProps {
  facility: any;
}

interface AssessmentRecord {
  id: string;
  date: string;
  scoreType: "Condition" | "Risk";
  score: number;
  status: string;
  assessedBy: string;
}

interface SystemAssetRecord {
  id: string;
  name: string;
  score: number;
  status: string;
}

export const ConditionsRisksTab: React.FC<ConditionsRisksTabProps> = ({
  facility,
}) => {
  const navigate = useNavigate();
  const facilityEntityType = 0;
  const assessmentName = "facility-assessment";
  const { id } = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: conditionAndRiskData } = useGetSystemConditionAndRiskById(
    assessmentName,
    Number(id)
  );
  const [currentPage, setCurrentPage] = useState(1);
  const { data: conditionHistory } = usegetConditionHistory(
    Number(id),
    facilityEntityType,
    currentPage
  );
  useEffect(() => {
    logEvent("conditions_tab_viewed");
  }, []);

  const assessCondition = useAssessCalculation(assessmentName, Number(id));
  const assessRisk = useAssessCalculation(assessmentName, Number(id));
  const [filteredData, setFilteredData] = useState<AssessmentRecord[]>(
    conditionHistory?.results || []
  );
  const [filters, setFilters] = useState({
    scoreType: "all",
    assessedBy: "all",
    startDate: "",
    endDate: "",
  });

  const PAGE_SIZE = 10;

  const getStatusBadgeConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case "good":
        return {
          variant: "default" as const,
          className: "bg-green-100 text-green-800",
        };
      case "fair":
      case "medium":
        return {
          variant: "secondary" as const,
          className: "bg-yellow-100 text-yellow-800",
        };
      case "poor":
      case "high":
        return {
          variant: "destructive" as const,
          className: "bg-red-100 text-red-800",
        };
      default:
        return {
          variant: "outline" as const,
          className: "bg-gray-100 text-gray-800",
        };
    }
  };

  const assessmentColumns: TableColumn<AssessmentRecord>[] = [
    {
      key: "assessmentTypeDisplay",
      header: "Score Type",
      type: "text",
      sortable: true,
      headerTooltip: "The type of assessment performed (Condition or Risk).",
      render: (value: string) => (
        <span className="font-medium ml-4">{value}</span>
      ),
    },
    {
      key: "score",
      header: "Score",
      type: "custom",
      render: (value: number, row: any) => (
        <span className="font-medium ml-4">
          {row.assessmentTypeDisplay === "Risk"
            ? row.riskScore
            : row.conditionScore}
        </span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      sortable: true,
      badgeConfig: {
        getVariant: (value: string) => getStatusBadgeConfig(value).variant,
        getClassName: (value: string) => getStatusBadgeConfig(value).className,
      },
      headerTooltip:
        "The qualitative, color-coded tag corresponding to the score.",
    },
    {
      key: "createdDate",
      header: "Assessment Date",
      type: "custom",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium ml-4">{value || "N/A"}</span>
      ),
      headerTooltip: "The date the assessment was officially recorded.",
    },
    {
      key: "createdUserRemoteName",
      header: "Assessed By",
      type: "text",
      sortable: true,
      headerTooltip:
        "The name and department of the user who performed the assessment.",
      render: (value: string) => (
        <span className="font-medium ml-4">{value || "N/A"}</span>
      ),
    },
  ];

  const systemLowestScoreColumns: TableColumn<SystemAssetRecord>[] = [
    {
      key: "systemName",
      header: "Name",
      type: "text",
      sortable: true,
    },
    {
      key: "score",
      header: "Score",
      type: "custom",
      sortable: true,
      render: (value: number) => (
        <span className="font-medium ml-4">{value}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      sortable: true,
      badgeConfig: {
        getVariant: (value: string) => getStatusBadgeConfig(value).variant,
        getClassName: (value: string) => getStatusBadgeConfig(value).className,
      },
    },
  ];

  const assetPoorScoreColumns: TableColumn<SystemAssetRecord>[] = [
    {
      key: "assetName",
      header: "Name",
      type: "text",
      sortable: true,
    },
    {
      key: "score",
      header: "Score",
      type: "custom",
      sortable: true,
      render: (value: number) => (
        <span className="font-medium ml-4">{value}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      sortable: true,
      badgeConfig: {
        getVariant: (value: string) => getStatusBadgeConfig(value).variant,
        getClassName: (value: string) => getStatusBadgeConfig(value).className,
      },
    },
  ];

  const systemHighestScoreColumns: TableColumn<SystemAssetRecord>[] = [
    {
      key: "systemName",
      header: "Name",
      type: "text",
      sortable: true,
    },
    {
      key: "score",
      header: "Score",
      type: "custom",
      sortable: true,
      render: (value: number) => (
        <span className="font-medium ml-4">{value}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      sortable: true,
      badgeConfig: {
        getVariant: (value: string) => getStatusBadgeConfig(value).variant,
        getClassName: (value: string) => getStatusBadgeConfig(value).className,
      },
    },
  ];

  const assetsHighestScoreColumns: TableColumn<SystemAssetRecord>[] = [
    {
      key: "assetName",
      header: "Name",
      type: "text",
      sortable: true,
    },
    {
      key: "score",
      header: "Score",
      type: "custom",
      sortable: true,
      render: (value: number) => (
        <span className="font-medium ml-4">{value}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      sortable: true,
      badgeConfig: {
        getVariant: (value: string) => getStatusBadgeConfig(value).variant,
        getClassName: (value: string) => getStatusBadgeConfig(value).className,
      },
    },
  ];

  const systemAssetActions: TableAction[] = [
    {
      label: "Schedule Inspection",
      onClick: (row: SystemAssetRecord) => {
        navigate(`/ax/service-order/landing/create`);
      },
      icon: "Calendar",
      variant: "outline",
    },
    {
      label: "Schedule Maintenance",
      onClick: (row: SystemAssetRecord) => {
        navigate(`/ax/service-order/landing/create`);
      },
      icon: "Wrench",
      variant: "outline",
    },
  ];

  const filterFields: FormField[] = [
    {
      name: "scoreType",
      type: "select",
      label: "Score Type",
      placeholder: "All Assessments",
      options: [
        { label: "All Assessments", value: "all" },
        { label: "Condition Assessments", value: "condition" },
        { label: "Risk Assessments", value: "risk" },
      ],
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: "w-full h-10 px-3 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "assessedBy",
      type: "select",
      label: "Assessed By",
      placeholder: "All Users",
      options: [
        { label: "All Users", value: "all" },
        { label: "Jane Doe", value: "Jane Doe" },
        { label: "John Smith", value: "John Smith" },
        { label: "Mike Johnson", value: "Mike Johnson" },
      ],
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: "w-full h-10 px-3 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "startDate",
      type: "datepicker",
      label: "Start Date",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-10 px-3 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "endDate",
      type: "datepicker",
      label: "End Date",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-10 px-3 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  const filterForm = FormService.createForm({
    fields: filterFields,
    defaultValues: filters,
  });

  const handleAssessCondition = () => {
    const payload = {
      isAccessCondition: true,
      entityType: 0,
      entityId: Number(id),
      remoteUtilityId: Number(remoteUtilityId),
    };
    assessCondition.mutateAsync(payload);
  };

  const handleAssessRisk = () => {
    const payload = {
      isAccessRisk: true,
      entityType: 0,
      entityId: Number(id),
      remoteUtilityId: Number(remoteUtilityId),
    };
    assessRisk.mutateAsync(payload);
  };

  const handleFilterReset = () => {
    const defaultFilters = {
      scoreType: "all",
      assessedBy: "all",
      startDate: "",
      endDate: "",
    };
    setFilters(defaultFilters);
    filterForm.reset(defaultFilters);
    setFilteredData(conditionHistory?.results || []);
    setCurrentPage(1);
  };

  const tabComponents = {
    overview: {
      label: "Overview",
      component: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Systems with Lowest Condition
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GenericTable
                  data={conditionAndRiskData?.systemsLowestCondition}
                  columns={systemLowestScoreColumns}
                  rowKey="id"
                  actions={systemAssetActions}
                  actionsType="dropdown"
                  emptyMessage="No systems found"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Systems with Highest Risk
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GenericTable
                  data={conditionAndRiskData?.systemsHighestRisk}
                  columns={systemHighestScoreColumns}
                  rowKey="id"
                  actions={systemAssetActions}
                  actionsType="dropdown"
                  emptyMessage="No systems found"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Assets in Poor Condition
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GenericTable
                  data={conditionAndRiskData?.assetsPoorCondition}
                  columns={assetPoorScoreColumns}
                  rowKey="id"
                  actions={systemAssetActions}
                  actionsType="dropdown"
                  emptyMessage="No assets found"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Assets with Highest Risk
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GenericTable
                  data={conditionAndRiskData?.assetsHighestRisk}
                  columns={assetsHighestScoreColumns}
                  rowKey="id"
                  actions={systemAssetActions}
                  actionsType="dropdown"
                  emptyMessage="No assets found"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      ),
    },
    history: {
      label: "History",
      component: (
        <div className="space-y-6">
          <GenericTable
            data={conditionHistory?.results}
            columns={assessmentColumns}
            rowKey="id"
            actionsType="icons"
            emptyMessage="No assessment history found"
            pagination={{
              currentPage,
              totalPages: Math.ceil(conditionHistory?.count / PAGE_SIZE),
              totalItems: conditionHistory?.count,
              itemsPerPage: PAGE_SIZE,
              startItem: (currentPage - 1) * PAGE_SIZE + 1,
              endItem: Math.min(
                currentPage * PAGE_SIZE,
                conditionHistory?.count
              ),
            }}
            onPageChange={setCurrentPage}
          />
        </div>
      ),
    },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Facility Condition */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xl font-semibold">
                  Facility Condition
                </CardTitle>
                <InfoTooltip
                  title="Facility Condition Score"
                  description="The overall health score of this facility, aggregated from its systems and assets."
                />
              </div>
              <Button
                isLoading={assessCondition.isPending}
                variant="outline"
                onClick={() => handleAssessCondition()}
              >
                Assess Condition
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl font-bold text-gray-900">
                {conditionAndRiskData?.lastConditionAssessment
                  ?.conditionScore || 0}
              </div>
              <Badge>
                {conditionAndRiskData?.lastConditionAssessment
                  ?.conditionRating || "N/A"}
              </Badge>
            </div>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <p>
                  Last Assessed:{" "}
                  {conditionAndRiskData?.lastConditionAssessment?.createdDate ||
                    "N/A"}
                </p>
                <InfoTooltip
                  title="Last Assessed Date"
                  description="The date the current condition score was officially recorded."
                  iconSize={12}
                />
              </div>
              <div className="flex items-center gap-1">
                <p>
                  Assessed By:{" "}
                  {conditionAndRiskData?.lastConditionAssessment
                    ?.createdUserRemoteName || "N/A"}
                </p>
                <InfoTooltip
                  title="Assessed By"
                  description="The user or team that performed the most recent condition assessment."
                  iconSize={12}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Facility Risk */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xl font-semibold">
                  Facility Risk
                </CardTitle>
                <InfoTooltip
                  title="Facility Risk Score"
                  description="A score representing the overall risk posed by this facility, combining likelihood and consequence of failure."
                />
              </div>
              <Button
                isLoading={assessRisk.isPending}
                variant="outline"
                onClick={() => handleAssessRisk()}
              >
                Assess Risk
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl font-bold text-gray-900">
                {conditionAndRiskData?.lastRiskAssessment?.riskScore || 0}
              </div>
              <Badge>
                {conditionAndRiskData?.lastRiskAssessment?.riskRating || "N/A"}
              </Badge>
            </div>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <p>
                  Last Assessed:{" "}
                  {conditionAndRiskData?.lastRiskAssessment?.createdDate ||
                    "N/A"}
                </p>
                <InfoTooltip
                  title="Last Assessed Date"
                  description="The date the current risk score was officially recorded."
                  iconSize={12}
                />
              </div>
              <div className="flex items-center gap-1">
                <p>
                  Assessed By:{" "}
                  {conditionAndRiskData?.lastRiskAssessment
                    ?.createdUserRemoteName || "N/A"}
                </p>
                <InfoTooltip
                  title="Assessed By"
                  description="The user or team that performed the most recent risk assessment."
                  iconSize={12}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs
        tabComponents={tabComponents}
        defaultValue="overview"
        urlMapping={{
          overview: "overview",
          history: "history",
        }}
        tabsListClassName="w-full grid grid-cols-2 bg-muted/30 p-1"
        idPrefix="conditions-risks-tab"
        level={1}
      />
    </div>
  );
};
